import React, { useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import {
  PLACE_HOLDER_WITHOUT_AI,
  PLACE_HOLDER_WITH_AI,
} from 'screens/common/MLCommentTextGenerationComp/constants/placeHolders';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';
import { checkAIReviewCommentsEnabled, checkReviewMobileFeedbackTypeEnabled } from 'utils/helpers/featureFlags';

import { REVIEW_ANSWER_TYPE_MAP } from 'constants/ProjectConstants';
import { getMultiSelectOptionsText } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { BaseStarRating } from 'uiToolkitV1/BaseStarRating';
import { ScaleRating } from 'uiToolkitV1/ScaleRating/ScaleRating';
import { TextBodyMedium, TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import DropDown from '../AnswerTypes/DropDown/DropDown';
import ReviewComment from '../ReviewComment/ReviewComment';
import { ReviewFeedbackTypeLabel } from './ReviewFeedbackTypeLabel';

const AnswerContainer = styled('div')`
  margin-bottom: 16px;
`;

export const ANSWER_TYPE_COMPONENTS = {
  [REVIEW_ANSWER_TYPE_MAP.DROP_DOWN]: {
    component: ({
      label,
      reactSelectAnswerOptions = [],
      selectedAnswerOptions = [],
      answerQuestionHandler,
      answerOptions,
      isDisabled,
      question,
      goalId,
    }) => {
      const answerOptionIds = selectedAnswerOptions?.map(({ id }) => id);
      const selectedValues = reactSelectAnswerOptions?.find(({ id }) => answerOptionIds?.indexOf(id) !== -1) ?? [];
      const answerDropDownQuestion = ({ answer }) => {
        const newSelectedAnswerOptionsIds = Array.isArray(answer) ? answer.map(({ id }) => id) : [answer.id];
        const newSelectedAnswerOptions =
          selectedAnswerOptions?.[0]?.id === newSelectedAnswerOptionsIds?.[0]
            ? []
            : answerOptions.filter(({ id }) => newSelectedAnswerOptionsIds.includes(id));

        answerQuestionHandler({ selectedAnswerOptions: newSelectedAnswerOptions });
      };
      return (
        <DropDown
          value={selectedValues}
          label={label}
          onChange={answerDropDownQuestion}
          options={reactSelectAnswerOptions}
          isDisabled={isDisabled}
          LabelComponent={
            checkReviewMobileFeedbackTypeEnabled() && isMobileView()
              ? () => <ReviewFeedbackTypeLabel label={label || 'Assessment'} question={question} goalId={goalId} />
              : null
          }
        />
      );
    },
  },
  [REVIEW_ANSWER_TYPE_MAP.MULTI_SELECT]: {
    component: ({
      label,
      reactSelectAnswerOptions = [],
      selectedAnswerOptions = [],
      answerQuestionHandler,
      answerOptions,
      question,
      isDisabled,
      goalId,
    }) => {
      const { minOptions, maxOptions } = question;
      const answerOptionIds = selectedAnswerOptions?.map(({ id }) => id);
      const selectedValues = reactSelectAnswerOptions?.filter(({ id }) => answerOptionIds?.indexOf(id) !== -1);
      const answerDropDownQuestion = ({ answer }) => {
        if (maxOptions && answer?.length > maxOptions) {
          return;
        }

        const selectedAnswerOptionsIds = Array.isArray(answer) ? answer.map(({ id }) => id) : [answer.id];
        const newSelectedAnswerOptions = answerOptions.filter(({ id }) => selectedAnswerOptionsIds.includes(id));

        answerQuestionHandler({ selectedAnswerOptions: newSelectedAnswerOptions });
      };
      return (
        <>
          <DropDown
            value={selectedValues}
            label={label}
            onChange={answerDropDownQuestion}
            options={reactSelectAnswerOptions}
            isMulti
            className={`questionId${question.id}`}
            isDisabled={isDisabled}
            LabelComponent={
              checkReviewMobileFeedbackTypeEnabled() && isMobileView()
                ? () => <ReviewFeedbackTypeLabel label={label || 'Assessment'} question={question} goalId={goalId} />
                : null
            }
          />
          <TextLabelSmall color="text.g3" mt="2px">
            {getMultiSelectOptionsText(minOptions, maxOptions, answerOptions.length)}
          </TextLabelSmall>
        </>
      );
    },
  },
  [REVIEW_ANSWER_TYPE_MAP.STAR_RATING]: {
    component: ({
      label,
      selectedAnswerOptions = [],
      isRequired,
      answerQuestionHandler,
      answerOptions,
      isDisabled,
    }) => {
      const answerStarRatingQuestion = (newRating) => {
        if (selectedAnswerOptions?.[0]?.displayOrder === newRating) {
          answerQuestionHandler({ selectedAnswerOptions: [] });
          return;
        }
        const newSelectedAnswerOptions = answerOptions.filter(({ displayOrder }) => displayOrder === newRating);
        answerQuestionHandler({ selectedAnswerOptions: newSelectedAnswerOptions });
      };
      return (
        <BaseStarRating
          label={label}
          handleChange={answerStarRatingQuestion}
          maxRating={answerOptions?.length}
          rating={selectedAnswerOptions[0]?.displayOrder || 0}
          isRequired={isRequired}
          disabled={isDisabled}
        />
      );
    },
  },
  [REVIEW_ANSWER_TYPE_MAP.NUMBER_RATING]: {
    component: ({ selectedAnswerOptions = [], answerQuestionHandler, answerOptions, isDisabled }) => {
      const answerScaleRatingQuestion = (selectedId) => {
        if (selectedAnswerOptions?.[0]?.id === selectedId) {
          answerQuestionHandler({ selectedAnswerOptions: [] });
          return;
        }
        const newSelectedAnswerOptions = answerOptions.filter((ans) => ans.id === selectedId);
        answerQuestionHandler({ selectedAnswerOptions: newSelectedAnswerOptions });
      };
      return (
        <ScaleRating
          options={answerOptions}
          selectedValue={selectedAnswerOptions[0]?.id || null}
          onSelectHandler={answerScaleRatingQuestion}
          isDisabled={isDisabled}
        />
      );
    },
  },
};

const QuestionRenderer = ({
  question,
  answerType,
  description,
  enableFeedbackText,
  answerOptions,
  feedbackText,
  feedbackTextHtml,
  isRequired,
  isTextRequired,
  showQuestionText = false,
  selectedAnswerOptions, // <- this is the already selected options which is coming from server, possible when user didn't fully submit the review
  answerQuestionHandler = () => {},
  questionIndex = 0,
  showQuestionIndex = false,
  isGoalSectionEditable = true,
  goalId,
}) => {
  const answerTypeComp = ANSWER_TYPE_COMPONENTS[answerType]?.component;
  const { employeeId, providerId, reviewId } = useReviewFormParams();
  const queryClient = useQueryClient();
  const sidebarData = queryClient.getQueryData([REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId]);
  const { reviewInfo } = sidebarData ?? {};
  const { providerType } = reviewInfo ?? {};

  const reactSelectAnswerOptions = useMemo(() => {
    if (answerType === REVIEW_ANSWER_TYPE_MAP.DROP_DOWN || answerType === REVIEW_ANSWER_TYPE_MAP.MULTI_SELECT) {
      // eslint-disable-next-line no-shadow
      return answerOptions?.map(({ id, description }) => ({
        label: description,
        value: id,
        id,
      }));
    }
    return [];
  }, [answerOptions, answerType]);

  const handleRTESubmit = (htmlText, plainText) => {
    answerQuestionHandler({ feedbackTextHtml: htmlText, feedbackText: plainText });
  };

  const reviewCommentPlaceholder = checkAIReviewCommentsEnabled() ? PLACE_HOLDER_WITH_AI : PLACE_HOLDER_WITHOUT_AI;
  const questionText = showQuestionIndex > 0 ? `${questionIndex + 1}. ${description}${isRequired ? '*' : ''}` : '';

  return (
    <Flex flexDir="column" mb={3} flexGrow={1}>
      {checkReviewMobileFeedbackTypeEnabled() &&
      isMobileView() &&
      answerType !== REVIEW_ANSWER_TYPE_MAP.DROP_DOWN &&
      answerType !== REVIEW_ANSWER_TYPE_MAP.MULTI_SELECT ? (
        <ReviewFeedbackTypeLabel
          // eslint-disable-next-line react/no-unstable-nested-components
          labelComp={() => <TextBodyMedium>{showQuestionText ? questionText : ''}</TextBodyMedium>}
          question={question}
          goalId={goalId}
        />
      ) : (
        showQuestionText && (
          <TextBodyMedium mb="16px">
            {showQuestionIndex > 0 ? `${questionIndex + 1}. ` : ''}
            {description} {isRequired ? '*' : ''}
          </TextBodyMedium>
        )
      )}
      {answerTypeComp && (
        <AnswerContainer>
          <>
            {answerTypeComp({
              answerType,
              description,
              enableFeedbackText,
              reactSelectAnswerOptions,
              selectedAnswerOptions,
              feedbackText,
              feedbackTextHtml,
              isRequired,
              answerOptions,
              question,
              goalId,
              answerQuestionHandler,
              isDisabled: !isGoalSectionEditable,
            })}
          </>
        </AnswerContainer>
      )}
      {enableFeedbackText && (
        <ReviewComment
          onBlur={handleRTESubmit}
          initialText={feedbackText}
          initialTextHTML={feedbackTextHtml}
          placeholder={`${reviewCommentPlaceholder} ${isRequired && isTextRequired ? '*' : ''}`}
          textGenerationProps={{
            questionId: question.id,
          }}
          key={feedbackTextHtml?.length ?? 0}
          providerType={providerType}
        />
      )}
    </Flex>
  );
};

const ViewOnlyQuestionRenderer = ({
  description,
  isRequired,
  questionIndex,
  showQuestionText = true,
  showQuestionIndex = false,
}) => (
  <Flex flexDir="column" mb={3} flexGrow={1}>
    {showQuestionText && (
      <>
        <TextLabel bold mb={4} color="text.g2">
          Questions answered by others
        </TextLabel>
        <TextBodyMedium mb="16px">
          {showQuestionIndex > 0 ? `${questionIndex + 1}. ` : ''}
          {description} {isRequired ? '*' : ''}
        </TextBodyMedium>
      </>
    )}
  </Flex>
);

export { QuestionRenderer, ViewOnlyQuestionRenderer };
